import React from "react";
import { Modal, Upload, Typography, Table, Button, Alert } from "antd";
import type { UploadProps } from "antd";
import { InboxOutlined, DownloadOutlined } from "@ant-design/icons";

const { Dragger } = Upload;
const { Text, Paragraph } = Typography;

interface UploadSmartButtonsModalProps {
  showUploadModal: boolean;
  toggleUploadModal: () => void;
  onUpload: (file: File) => void;
  uploading: boolean;
}

const TEMPLATE_COLUMNS = [
  { title: "Column", dataIndex: "column", key: "column" },
  { title: "Description", dataIndex: "description", key: "description" },
  { title: "Example", dataIndex: "example", key: "example" },
];

const TEMPLATE_ROWS = [
  { key: "1", column: "serial_number", description: "Smart button's serial number", example: "SB-00123" },
  { key: "2", column: "bdaddr", description: "Smart button's MAC / Bluetooth address", example: "aa:bb:cc:dd:ee:ff" },
];

const downloadTemplate = () => {
  const csvContent = "serial_number,bdaddr\nSB-00123,aa:bb:cc:dd:ee:ff\n";
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "smart_buttons_template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const UploadSmartButtonsModal: React.FC<UploadSmartButtonsModalProps> = ({
  showUploadModal,
  toggleUploadModal,
  onUpload,
  uploading,
}) => {
  const draggerProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    showUploadList: false,
    disabled: uploading,
    beforeUpload: (file) => {
      onUpload(file);
      return false;
    },
  };

  return (
    <Modal
      title="Upload Smart Buttons List"
      open={showUploadModal}
      onCancel={toggleUploadModal}
      footer={[
        <Button key="close" onClick={toggleUploadModal}>
          Close
        </Button>,
      ]}
    >
      <Paragraph>
        The file must be a <Text code>.csv</Text> with the columns below — this is the format Excel
        produces via <Text code>File → Save As → CSV</Text>. Any row missing either column is skipped,
        and serial numbers already in the system are left untouched rather than overwritten.
      </Paragraph>
      <Table
        columns={TEMPLATE_COLUMNS}
        dataSource={TEMPLATE_ROWS}
        pagination={false}
        size="small"
        style={{ marginBottom: 16 }}
      />
      <Button icon={<DownloadOutlined />} onClick={downloadTemplate} style={{ marginBottom: 16 }}>
        Download CSV Template
      </Button>
      <Dragger {...draggerProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag a CSV file to this area to upload</p>
        <p className="ant-upload-hint">Only .csv files are accepted.</p>
      </Dragger>
      {uploading && <Alert style={{ marginTop: 16 }} message="Uploading..." type="info" showIcon />}
    </Modal>
  );
};

export default UploadSmartButtonsModal;
